import { useState, useEffect } from "react";
import { useToast } from "@chakra-ui/react";
import {
  Container,
  Box,
  VStack,
  StackDivider,
  useColorModeValue,
  Heading,
  Text,
  Radio,
  RadioGroup,
  Skeleton,
  SimpleGrid,
  Select,
  Button,
  HStack,
} from "@chakra-ui/react";
import { useParams, Link } from "react-router-dom";
import { Line } from "react-chartjs-2";
import { t } from "@lingui/core/macro";
import useAxios from "../../utils/useAxios";
import { MoistureIndexThresholds } from "../../utils/colorUtils/mapColorUtils";
import { createMoistureIndexShading } from "../map/moistureIndexChartPlugin";

const fullDateTimeFormat: Intl.DateTimeFormatOptions = {
  year: "2-digit",
  month: "2-digit",
  day: "2-digit",
  hour: "2-digit",
  minute: "2-digit",
};

const hourlyDateTimeFormat: Intl.DateTimeFormatOptions = {
  year: "2-digit",
  month: "2-digit",
  day: "2-digit",
  hour: "2-digit",
};

const dateOnlyFormat: Intl.DateTimeFormatOptions = {
  year: "2-digit",
  month: "2-digit",
  day: "2-digit",
};

interface IData {
  label: string;
  y_axis: string;
  timestamps: string[];
  series: Array<{ label: string; data: Array<number | null> }>;
}

interface IPlottableDeviceData {
  soil_moisture?: Array<number | null>;
  soil_moisture_2?: Array<number | null>;
  soil_moisture_3?: Array<number | null>;
  moisture_index_1?: Array<number | null>;
  moisture_index_2?: Array<number | null>;
  moisture_index_3?: Array<number | null>;
  soil_temperature?: Array<number | null>;
  soil_temperature_2?: Array<number | null>;
  soil_temperature_3?: Array<number | null>;
  soil_conductivity?: Array<number | null>;
  soil_conductivity_2?: Array<number | null>;
  soil_conductivity_3?: Array<number | null>;
  air_temperature?: Array<number | null>;
  humidity?: Array<number | null>;
  lux?: Array<number | null>;
  battery_voltage?: Array<number | null>;
  battery?: Array<number | null>;
  timestamp: Array<string>; // Can be Date if directly working with Date objects
  formatted_timestamp: Array<string>;
}

const getChartGroupKey = (key: string) =>
  /^moisture_index_[123]$/.test(key)
    ? "moisture_index"
    : key === "air_temperature" || /^soil_temperature(?:_[23])?$/.test(key)
      ? "temperature"
      : /^soil_(?:moisture|conductivity)_[23]$/.test(key)
        ? key.replace(/_[23]$/, "")
        : key;

const getValidSeriesKeys = (
  data: IPlottableDeviceData,
  groupKey: string,
  isKeyVisible: (key: string) => boolean = () => true,
) =>
  Object.keys(data).filter((key) => {
    const series = data[key as keyof IPlottableDeviceData];
    return (
      getChartGroupKey(key) === groupKey &&
      isKeyVisible(key) &&
      Array.isArray(series) &&
      series.some((item) => item !== null)
    );
  });

interface IPlotSettings {
  [key: string]: { min?: number; max?: number } | undefined;
  soil_moisture?: { min?: number; max?: number };
  soil_moisture_2?: { min?: number; max?: number };
  soil_moisture_3?: { min?: number; max?: number };
  soil_temperature?: { min?: number; max?: number };
  soil_temperature_2?: { min?: number; max?: number };
  soil_temperature_3?: { min?: number; max?: number };
  soil_conductivity?: { min?: number; max?: number };
  soil_conductivity_2?: { min?: number; max?: number };
  soil_conductivity_3?: { min?: number; max?: number };
  air_temperature?: { min?: number; max?: number };
  humidity?: { min?: number; max?: number };
  lux?: { min?: number; max?: number };
  battery_voltage?: { min?: number; max?: number };
  battery?: { min?: number; max?: number };
  // Add other settings here
}

interface IDataGraphProps {
  data: IData | undefined;
  chartDataIsLoading: boolean;
  plotSettings?: IPlotSettings;
}

const plotSettings: IPlotSettings = {
  soil_moisture: { min: 20, max: 40 },
  soil_moisture_2: { min: 20, max: 40 },
  soil_moisture_3: { min: 20, max: 40 },
  moisture_index_1: { min: 0, max: 1 },
  moisture_index_2: { min: 0, max: 1 },
  moisture_index_3: { min: 0, max: 1 },
  moisture_index: { min: 0, max: 1 },
  humidity: { min: 0, max: 100 },
};

const DataGraph = ({
  data,
  chartDataIsLoading,
  plotSettings,
  moistureIndexThresholds = {},
}: {
  data: IData | undefined;
  chartDataIsLoading: boolean;
  plotSettings: IPlotSettings;
  moistureIndexThresholds?: MoistureIndexThresholds;
}) => {
  console.log(data);
  console.log(chartDataIsLoading);

  if (!data || chartDataIsLoading) {
    return <Skeleton height="300px" width="100%" />;
  }

  console.log(plotSettings[data.label]);
  console.log(data);

  const isMoistureIndex =
    data.label === "moisture_index" || data.label.startsWith("moisture_index_");
  const seriesColor = (label: string, index: number) => {
    const colorBySeries: Record<string, string> = {
      soil_moisture: "rgba(100, 181, 246, 1)",
      soil_moisture_2: "rgba(33, 150, 243, 1)",
      soil_moisture_3: "rgba(13, 71, 161, 1)",
      moisture_index_1: "rgba(100, 181, 246, 1)",
      moisture_index_2: "rgba(33, 150, 243, 1)",
      moisture_index_3: "rgba(13, 71, 161, 1)",
      soil_conductivity: "rgba(165, 214, 167, 1)",
      soil_conductivity_2: "rgba(76, 175, 80, 1)",
      soil_conductivity_3: "rgba(27, 94, 32, 1)",
      air_temperature: "rgba(255, 167, 38, 1)",
      soil_temperature: "rgba(239, 83, 80, 1)",
      soil_temperature_2: "rgba(198, 40, 40, 1)",
      soil_temperature_3: "rgba(136, 14, 79, 1)",
    };
    const fallbackColors = [
      "rgba(0, 121, 107, 1)",
      "rgba(94, 53, 177, 1)",
      "rgba(117, 117, 117, 1)",
    ];
    return colorBySeries[label] ?? fallbackColors[index % fallbackColors.length];
  };

  return (
    <Line
      key={
        isMoistureIndex
          ? `${data.label}:${moistureIndexThresholds.management}:${moistureIndexThresholds.deficit}:${moistureIndexThresholds.upperManagement}`
          : data.label
      }
      plugins={
        isMoistureIndex ? [createMoistureIndexShading(moistureIndexThresholds, "y")] : []
      }
      height={180}
      data={{
        labels: data.timestamps,
        datasets: data.series.map((series, index) => ({
          label: series.label.replace(/_/g, " "),
          data: series.data,
          fill: data.label === "soil_moisture",
          backgroundColor: isMoistureIndex
            ? "rgba(105,75,180,0.2)"
            : "rgba(75, 192, 192, 0.1)",
          borderColor: seriesColor(series.label, index),
          pointRadius: 0,
        })),
      }}
      options={{
        tooltips: {
          mode: "index",
          intersect: false,
          ...(isMoistureIndex && {
            callbacks: {
                label: (
                  tooltipItem: { datasetIndex: number; yLabel: number },
                  chartData: { datasets: Array<{ label?: string }> },
                ) => {
                  const label = chartData.datasets[tooltipItem.datasetIndex]?.label ?? "";
                  return `${label}${label ? ": " : ""}${tooltipItem.yLabel.toFixed(2)}`;
                },
            },
          }),
        },
        scales: {
          yAxes: [
            {
              id: "y",
              type: "linear",
              position: "left",
              ticks: {
                suggestedMin: plotSettings?.[data.label]?.min,
                suggestedMax: plotSettings?.[data.label]?.max,
              },
            },
          ],
        },
      }}
    />
  );
};

const DevicePlot = ({
  hideSoilMetrics = false,
  moistureIndexThresholds,
}: {
  hideSoilMetrics?: boolean;
  moistureIndexThresholds?: MoistureIndexThresholds;
}) => {
  const api = useAxios();
  const toast = useToast();

  const { id } = useParams<{ id: string }>();

  const [days, setDays] = useState<number>(7); // Default to 7 days

  const [chartDataIsLoading, setChartDataIsLoading] = useState(true);
  const [chartData, setChartData] = useState<IPlottableDeviceData | null>(null);
  const [selectedChart, setSelectedChart] = useState<IData | undefined>(undefined);
  const [selectedChartId, setSelectedChartId] = useState<string>("soil_moisture");

  const isVisibleChartKey = (key: string) =>
    !hideSoilMetrics ||
    ![
      "soil_moisture",
      "soil_moisture_2",
      "soil_moisture_3",
      "moisture_index_1",
      "moisture_index_2",
      "moisture_index_3",
      "moisture_index",
      "soil_temperature",
      "soil_temperature_2",
      "soil_temperature_3",
      "soil_conductivity",
      "soil_conductivity_2",
      "soil_conductivity_3",
    ].includes(key);

  const fetchChartData = async () => {
    let dateFormat: Intl.DateTimeFormatOptions;
    try {
      let aggregate = "none";
      if (days > 30) {
        aggregate = "date";
        dateFormat = dateOnlyFormat;
      } else if (days > 1) {
        aggregate = "hour";
        dateFormat = hourlyDateTimeFormat;
      } else {
        aggregate = "none";
        dateFormat = fullDateTimeFormat;
      }
      let response = await api.get("frontend/devices/plottable-data/" + id + "/", {
        params: {
          days: days,
          aggregate: aggregate,
        },
      });

      console.log(response.data.timestamp);

      response.data.formatted_timestamp = response.data.timestamp?.map((timestamp: string) =>
        new Date(timestamp).toLocaleDateString("nb-NO", dateFormat),
      );

      // Check whether the currently selected chart ID is valid (exists and has non-null data)
      const isCurrentDataValid =
        isVisibleChartKey(selectedChartId) &&
        getValidSeriesKeys(response.data, selectedChartId, isVisibleChartKey).length > 0;

      if (!isCurrentDataValid) {
        // Find the first valid key
        const firstValidKey = Object.keys(response.data).find((key) => {
          const data = response.data[key];
          return (
            key !== "timestamp" &&
            key !== "formatted_timestamp" &&
            isVisibleChartKey(getChartGroupKey(key)) &&
            getValidSeriesKeys(response.data, getChartGroupKey(key), isVisibleChartKey).length > 0
          );
        });

        if (firstValidKey) {
          setSelectedChartId(getChartGroupKey(firstValidKey));
        }
      }

      console.log(response.data);
      setChartData(response.data);

      setChartDataIsLoading(false);
    } catch {
      setChartData(null);
      setChartDataIsLoading(false);
      // Toast
      toast({
        title: "Error fetching data",
        status: "error",
        duration: 9000,
        isClosable: true,
      });
    }
  };

  useEffect(() => {
    fetchChartData();
  }, []);

  // Fetch new plot data when days changes
  useEffect(() => {
    setChartDataIsLoading(true);
    fetchChartData();
  }, [days]);

  useEffect(() => {
    if (chartData) {
      const seriesKeys = getValidSeriesKeys(chartData, selectedChartId, isVisibleChartKey);
      if (isVisibleChartKey(selectedChartId) && seriesKeys.length > 0) {
        setSelectedChart({
          label: selectedChartId,
          y_axis: selectedChartId,
          timestamps: chartData.formatted_timestamp,
          series: seriesKeys.map((key) => ({
            label: key,
            data: chartData[key as keyof IPlottableDeviceData] as Array<number | null>,
          })),
        });
      }
    }
  }, [chartData, hideSoilMetrics, selectedChartId]);

  const heading_color = useColorModeValue("black", "gray.200");

  return (
    <Box>
      <HStack justify="space-between" mb={4}>
        <Heading as="h2" size="lg" color={heading_color}>
          Chart
        </Heading>
        <Button
          as={Link}
          to={`/devices/${id}/compare?left_type=sensor&left_device=${id}&days=7&resolution=hourly`}
          borderRadius="unset"
          p={2}
          size="sm"
          colorScheme="black"
          variant="outline"
          _hover={{ bg: "blackAlpha.100" }}
        >
          Compare
        </Button>
      </HStack>
      <SimpleGrid minChildWidth="300px" spacing="20px">
        <RadioGroup onChange={(value: string) => setDays(Number(value))} value={days.toString()}>
          <Radio mr={3} value={"1"}>
            1 day
          </Radio>
          <Radio mr={3} value={"7"}>
            7 days
          </Radio>
          <Radio mr={3} value={"30"}>
            30 days
          </Radio>
          <Radio mr={3} value={"90"}>
            90 days
          </Radio>
          <Radio value={"365"}>365 days</Radio>
        </RadioGroup>
        <Select
          placeholder={t`Select data to plot`}
          value={selectedChartId}
          onChange={(event) => setSelectedChartId(event.target.value)}
        >
          {chartData &&
            Array.from(new Set(Object.keys(chartData).map(getChartGroupKey))).map((key) => {
              const chartDataKey = key as keyof IPlottableDeviceData;
              if (
                chartDataKey !== "timestamp" &&
                chartDataKey !== "formatted_timestamp" &&
                isVisibleChartKey(chartDataKey) &&
                getValidSeriesKeys(chartData, key, isVisibleChartKey).length > 0
              ) {
                return (
                  <option value={chartDataKey} key={chartDataKey}>
                    {chartDataKey.replace(/_/g, " ")}
                  </option>
                );
              }
            })}
        </Select>
      </SimpleGrid>
      <DataGraph
        data={selectedChart}
        chartDataIsLoading={chartDataIsLoading}
        plotSettings={plotSettings}
        moistureIndexThresholds={moistureIndexThresholds}
      />
    </Box>
  );
};

export default DevicePlot;
